import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * 交給麥閒助理（2026-10-19）：按一下，Mac 上的麥閒助理 App 用 Max 額度整理，好了 LINE 通知。
 * 網頁只送出「請處理這場」，不在瀏覽器裡跑 AI（守 mcp-local-ui-principle）。
 * 送出後每 15 秒看一次這場的狀態，整理好就自動重整頁面。
 */
export function AssistantHandoff({ meetingId }: { meetingId: string }) {
  const [state, setState] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [note, setNote] = useState("");

  async function send() {
    setState("sending");
    setNote("");
    try {
      const r = await api.sendToAssistant(meetingId);
      setState("sent");
      setNote(
        r.app_online
          ? "已交給麥閒助理，約 2–5 分鐘。整理好會用 LINE 通知你，這頁也會自動更新。"
          : "已交給麥閒助理，但電腦現在沒開。開機後會自動接著做，整理好用 LINE 通知你。",
      );
    } catch (e) {
      setState("error");
      setNote((e as Error).message || "送出失敗，請稍後再試，或改用下面的 Claude Code 指令。");
    }
  }

  useEffect(() => {
    if (state !== "sent") return;
    let n = 0;
    const t = window.setInterval(async () => {
      n++;
      try {
        const { meeting } = await api.getMeeting(meetingId);
        if (meeting.status === "completed") window.location.reload();
      } catch {
        /* 下一輪再看 */
      }
      if (n >= 80) window.clearInterval(t); // 最多看 20 分鐘
    }, 15000);
    return () => window.clearInterval(t);
  }, [state, meetingId]);

  return (
    <div className="rounded-lg border border-brand-gold/40 bg-brand-gold/5 p-4 sm:p-5 min-w-0 mb-6">
      <div className="flex items-start justify-between gap-3 flex-wrap min-w-0">
        <div className="min-w-0 flex-1 basis-60">
          <div className="text-sm font-bold text-brand-dark">交給麥閒助理</div>
          <p className="text-xs text-brand-dark/60 leading-relaxed mt-1" style={{ overflowWrap: "anywhere" }}>
            不用開終端機。電腦上的麥閒助理用 <span className="whitespace-nowrap">Claude Max</span> 額度整理，好了用{" "}
            <span className="whitespace-nowrap">LINE</span> 傳連結給你。
          </p>
        </div>
        <button
          onClick={send}
          disabled={state === "sending" || state === "sent"}
          className={cn(
            "px-5 py-2.5 rounded-full text-sm font-medium transition-colors whitespace-nowrap",
            state === "sent"
              ? "bg-brand-green text-white"
              : "bg-brand-dark text-brand-cream hover:bg-brand-gold disabled:opacity-60",
          )}
        >
          {state === "sending" ? "送出中…" : state === "sent" ? "已交給麥閒助理" : "交給麥閒助理"}
        </button>
      </div>
      {note && (
        <p
          className={cn("text-xs leading-relaxed mt-3", state === "error" ? "text-red-700" : "text-brand-dark/70")}
          style={{ overflowWrap: "anywhere" }}
        >
          {note}
        </p>
      )}
    </div>
  );
}
